import { ConvexError } from "convex/values";

export interface AuthErrorResult {
  message: string;
  type:
    | "validation"
    | "authentication"
    | "authorization"
    | "network"
    | "unknown";
  code?: string;
}

function extractCode(data: unknown): string | undefined {
  if (typeof data === "object" && data !== null) {
    const obj = data as Record<string, unknown>;

    if (typeof obj.code === "string") {
      return obj.code;
    }
  }

  return undefined;
}

function extractMessage(data: unknown): string | undefined {
  if (typeof data === "string") {
    return data;
  }

  if (typeof data === "object" && data !== null) {
    const obj = data as Record<string, unknown>;

    if (typeof obj.message === "string") {
      return obj.message;
    }

    if (typeof obj.data === "string") {
      return obj.data;
    }

    if (typeof obj.data === "object" && obj.data !== null) {
      const nestedData = obj.data as Record<string, unknown>;
      if (typeof nestedData.message === "string") {
        return nestedData.message;
      }
    }
  }

  return undefined;
}

export function handleAuthError(error: unknown): AuthErrorResult {
  if (error instanceof ConvexError) {
    const code = extractCode(error.data);
    const message = extractMessage(error.data) || "Something went wrong";

    return {
      message,
      type: "unknown",
      code,
    };
  }

  if (error instanceof Error) {
    if (
      error.message.includes("InvalidSecret") ||
      error.message.includes("InvalidAccountId")
    ) {
      return {
        message: "Invalid email or password",
        type: "authentication",
        code: "invalid_credentials",
      };
    }

    const errorWithData = error as Error & { data?: unknown };
    if (errorWithData.data) {
      const extractedMessage = extractMessage(errorWithData.data);
      if (extractedMessage) {
        return {
          message: extractedMessage,
          type: "unknown",
          code: extractCode(errorWithData.data),
        };
      }
    }

    if (error.message.includes("[CONVEX")) {
      return {
        message: "Something went wrong. Please try again.",
        type: "unknown",
        code: "convex_error",
      };
    }

    return {
      message: error.message || "Something went wrong",
      type: "unknown",
      code: "unknown_error",
    };
  }

  const extractedMessage = extractMessage(error);
  if (extractedMessage) {
    return {
      message: extractedMessage,
      type: "unknown",
      code: extractCode(error),
    };
  }

  return {
    message: "Something went wrong",
    type: "unknown",
    code: "unknown_error",
  };
}
